import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Briefcase, ShieldCheck, Users, Palette } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAdvisor } from "../../context/AdvisorContext";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { NoIndex } from "../../components/Seo";

const formatDetail = (d) => (Array.isArray(d) ? d.map((e) => e.msg || JSON.stringify(e)).join(" ") : d || "Something went wrong");

const GoogleButton = () => {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/advisor";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" data-testid="advisor-google-btn" onClick={go} className="w-full rounded-full h-11 gap-2">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.5 13.3l7.9 6.1C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v8.1h12.9c-.3 2.1-1.7 5.3-4.8 7.4l7.4 5.7c4.4-4.1 7-10.1 7-17.2z"/><path fill="#FBBC05" d="M10.4 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.5 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2 1.4-4.7 2.4-8.5 2.4-6.3 0-11.7-4.1-13.6-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
      Continue with Google
    </Button>
  );
};

const Field = ({ id, label, ...props }) => (
  <label className="block text-sm">
    <span className="text-xs uppercase tracking-wider font-mono text-muted-foreground">{label}</span>
    <Input id={id} data-testid={`advisor-${id}`} className="mt-1 h-11 rounded-xl" {...props} />
  </label>
);

export default function AdvisorLogin() {
  const { login, register } = useAdvisor();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "", firm_name: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login({ email: f.email, password: f.password });
      else await register(f);
      navigate("/advisor", { replace: true });
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-2" data-testid="advisor-login-page">
      <NoIndex title="Advisor portal" />
      <aside className="hidden lg:flex flex-col justify-between p-12 bg-foreground text-background">
        <Link to="/" className="font-serif text-2xl font-semibold">HearthVault <span className="text-primary">for Advisors</span></Link>
        <div className="max-w-md">
          <h1 className="font-serif text-4xl leading-tight">Give every client a vault they'll actually keep up.</h1>
          <ul className="mt-8 space-y-4 text-sm text-background/80">
            <li className="flex gap-3"><Users className="h-5 w-5 text-primary shrink-0" /> Create client vaults in a minute — the family owns the data, you get read-only visibility they can switch off anytime.</li>
            <li className="flex gap-3"><Palette className="h-5 w-5 text-primary shrink-0" /> Your firm's name, logo and colour on the lock screen, emails and the executor PDF. "Powered by HearthVault".</li>
            <li className="flex gap-3"><ShieldCheck className="h-5 w-5 text-primary shrink-0" /> Every client vault gets Family-tier features: AI filing, estate library, beneficiaries, bank linking, bereavement pack.</li>
            <li className="flex gap-3"><Briefcase className="h-5 w-5 text-primary shrink-0" /> $49/mo or $490/yr for 10 client vaults; $4/mo per extra. Your first client vault is free while you try it.</li>
          </ul>
        </div>
        <div className="text-xs text-background/50">No trackers, no ads — same privacy promise as the family app.</div>
      </aside>
      <main className="grid place-items-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="advisor-auth-form">
          <div className="lg:hidden font-serif text-xl font-semibold mb-6">HearthVault <span className="text-primary">for Advisors</span></div>
          <div className="flex rounded-full bg-muted p-1 text-sm mb-6">
            {["login", "register"].map((m) => (
              <button key={m} type="button" data-testid={`advisor-mode-${m}`} onClick={() => setMode(m)} className={`flex-1 rounded-full py-2 transition-colors ${mode === m ? "bg-card shadow font-medium" : "text-muted-foreground"}`}>
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>
          <h2 className="font-serif text-2xl font-medium">{mode === "login" ? "Welcome back" : "Set up your firm"}</h2>
          <div className="mt-5 space-y-3">
            {mode === "register" && <Field id="name" label="Your name" value={f.name} onChange={set("name")} required />}
            {mode === "register" && <Field id="firm-name" label="Firm name" value={f.firm_name} onChange={set("firm_name")} placeholder="Reyes Estate Law" />}
            <Field id="email" label="Work email" type="email" value={f.email} onChange={set("email")} required autoComplete="email" />
            <Field id="password" label="Password" type="password" value={f.password} onChange={set("password")} required minLength={8} autoComplete={mode === "login" ? "current-password" : "new-password"} />
          </div>
          <Button type="submit" disabled={busy} data-testid="advisor-submit-btn" className="mt-5 w-full h-11 rounded-full">{busy ? "One moment…" : mode === "login" ? "Sign in" : "Create advisor account"}</Button>
          <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" /></div>
          <GoogleButton />
          <p className="mt-6 text-xs text-muted-foreground">Looking for your family vault? <Link to="/vault" className="text-primary hover:underline">Enter the vault</Link>.</p>
        </form>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
